import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MagnifyingGlass, Spinner, Plus, Trash, Target, Fire, Heartbeat, Barbell, Camera, X, CheckCircle } from "@phosphor-icons/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function MacroLookup({ api, profile }) {
  const [activeTab, setActiveTab] = useState("tracker");

  // Macro lookup state
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rawText, setRawText] = useState("");

  // Calorie tracker state
  const [todayDate, setTodayDate] = useState(new Date().toISOString().split("T")[0]);
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({ calories: 0, protein: 0, carbs: 0, fat: 0 });
  const [goal, setGoal] = useState(2000);
  const [showAddFood, setShowAddFood] = useState(false);
  const [showGoalEdit, setShowGoalEdit] = useState(false);
  const [newGoal, setNewGoal] = useState("2000");
  const [foodForm, setFoodForm] = useState({ food_name: "", calories: "", protein: "", carbs: "", fat: "", serving: "" });
  // Scan state
  const [showScan, setShowScan] = useState(false);
  const [scanPreview, setScanPreview] = useState("");      // dataURL for thumbnail
  const [scanAnalyzing, setScanAnalyzing] = useState(false);
  const [scanResult, setScanResult] = useState(null);       // {name, serving, calories, protein, carbs, fat, fiber, confidence, notes}
  const [scanError, setScanError] = useState("");
  const [savingScan, setSavingScan] = useState(false);
  const [savingFood, setSavingFood] = useState(false);
  const [basePer100, setBasePer100] = useState(null);
  const [servingGrams, setServingGrams] = useState("100");
  const [lookingUp, setLookingUp] = useState(false);
  const [recs, setRecs] = useState(null);

  const fetchCalorieData = useCallback(async () => {
    try {
      const [logRes, goalRes, recRes] = await Promise.all([
        axios.get(`${api}/calorie-log?date=${todayDate}`),
        axios.get(`${api}/calorie-goal`),
        axios.get(`${api}/nutrition-recommendations`),
      ]);
      setEntries(logRes.data.entries || []);
      setTotals(logRes.data.totals || { calories: 0, protein: 0, carbs: 0, fat: 0 });
      setGoal(goalRes.data.daily_calories || 2000);
      setNewGoal(String(goalRes.data.daily_calories || 2000));
      if (recRes.data) setRecs(recRes.data);
    } catch (e) {
      console.error("Calorie fetch error:", e);
    }
  }, [api, todayDate]);

  useEffect(() => {
    fetchCalorieData();
  }, [fetchCalorieData]);

  // Macro lookup
  const searchFood = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setRawText("");
    try {
      const res = await axios.post(`${api}/ai/food-macros`, { query: query.trim() });
      if (res.data.macros) {
        setResults((prev) => [res.data.macros, ...prev]);
        setRawText("");
      } else {
        setRawText(res.data.raw || "Could not parse nutrition data");
      }
      setQuery("");
    } catch (e) {
      toast.error("Failed to look up food macros");
    } finally {
      setLoading(false);
    }
  };

  // Add food from macro result to calorie log
  const addToLog = async (food) => {
    try {
      await axios.post(`${api}/calorie-log`, {
        date: todayDate,
        food_name: food.name,
        calories: food.calories || 0,
        protein: food.protein || 0,
        carbs: food.carbs || 0,
        fat: food.fat || 0,
        serving: food.serving || "100g",
      });
      toast.success(`Added ${food.name} to today's log`);
      fetchCalorieData();
    } catch (e) {
      toast.error("Failed to add to log");
    }
  };

  // AI lookup for the food form
  const lookUpFood = async () => {
    if (!foodForm.food_name.trim()) return;
    setLookingUp(true);
    try {
      const res = await axios.post(`${api}/ai/food-macros`, { query: foodForm.food_name.trim() });
      if (res.data.macros) {
        const m = res.data.macros;
        setBasePer100({
          calories: m.calories || 0,
          protein: m.protein || 0,
          carbs: m.carbs || 0,
          fat: m.fat || 0,
        });
        const g = parseFloat(servingGrams) || 100;
        const ratio = g / 100;
        setFoodForm(prev => ({
          ...prev,
          calories: String(Math.round((m.calories || 0) * ratio)),
          protein: String(+((m.protein || 0) * ratio).toFixed(1)),
          carbs: String(+((m.carbs || 0) * ratio).toFixed(1)),
          fat: String(+((m.fat || 0) * ratio).toFixed(1)),
          serving: `${g}g`,
        }));
        toast.success(`Found: ${m.name || foodForm.food_name}`);
      } else {
        toast.error("Couldn't find nutrition data. Enter manually.");
      }
    } catch (e) {
      toast.error("Lookup failed. Enter values manually.");
    } finally {
      setLookingUp(false);
    }
  };

  // Recalculate when serving grams change and we have base data
  const handleServingChange = (val) => {
    setServingGrams(val);
    if (basePer100) {
      const g = parseFloat(val) || 0;
      const ratio = g / 100;
      setFoodForm(prev => ({
        ...prev,
        calories: String(Math.round(basePer100.calories * ratio)),
        protein: String(+(basePer100.protein * ratio).toFixed(1)),
        carbs: String(+(basePer100.carbs * ratio).toFixed(1)),
        fat: String(+(basePer100.fat * ratio).toFixed(1)),
        serving: `${g}g`,
      }));
    }
  };

  // ── Scan via phone camera ──
  // Compute a goal-aware projection for what logging the scanned food will do to today's totals.
  // Returns { projected, remaining, percent, tone, label, tip } or null if no data yet.
  const scanProjection = (() => {
    if (!scanResult || !recs?.calories) return null;
    const eaten = totals?.calories || 0;
    const adding = Number(scanResult.calories) || 0;
    const target = recs.calories;
    const projected = Math.round(eaten + adding);
    const remaining = Math.round(target - projected);
    const percent = Math.min(Math.round((projected / target) * 100), 999);
    const goal = recs.primary_goal || "general_fitness";
    const cutting = goal === "lose_weight";
    const bulking = goal === "gain_mass" || goal === "gain_strength";

    let tone = "neutral"; // neutral | good | warn | bad
    let label;
    let tip = "";
    if (remaining < 0) {
      // Over the target
      tone = cutting ? "bad" : bulking ? "good" : "warn";
      const overBy = Math.abs(remaining);
      label = `${overBy} kcal over your goal`;
      if (cutting) tip = `That breaks today's deficit. Smaller portion or save part for tomorrow?`;
      else if (bulking) tip = `Past your target — perfect for a gainer day. Keep going.`;
      else tip = `You'll be slightly over your maintenance target.`;
    } else if (remaining <= target * 0.05) {
      tone = "good";
      label = `You'll hit your goal exactly`;
    } else {
      // Under
      tone = bulking ? (remaining > target * 0.4 ? "warn" : "neutral") : "good";
      label = `${remaining} kcal left after this`;
      if (bulking && remaining > target * 0.4) tip = `You'll still be well under target — add another snack later to keep building.`;
    }
    return { projected, remaining, percent, tone, label, tip, target };
  })();
  // Resize the captured image client-side before upload to keep the request fast
  // and stay well under proxy limits. Max 1280px on the longest edge.
  const fileToCompressedBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1280;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          const scale = MAX / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        resolve(dataUrl);
      };
      img.onerror = () => reject(new Error("Could not read image"));
      img.src = reader.result;
    };
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });

  const handleScanFile = async (file) => {
    if (!file) return;
    setScanError("");
    setScanResult(null);
    setScanAnalyzing(true);
    try {
      const dataUrl = await fileToCompressedBase64(file);
      setScanPreview(dataUrl);
      const base64 = dataUrl.split(",", 2)[1];
      const res = await axios.post(`${api}/ai/food-photo`, {
        image_base64: base64,
        mime_type: "image/jpeg",
      });
      setScanResult(res.data?.macros || null);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not analyse photo. Try again.";
      setScanError(typeof msg === "string" ? msg : "Could not analyse photo.");
    } finally {
      setScanAnalyzing(false);
    }
  };

  const resetScan = () => {
    setScanPreview("");
    setScanResult(null);
    setScanError("");
    setScanAnalyzing(false);
  };

  const saveScanResult = async () => {
    if (!scanResult) return;
    setSavingScan(true);
    try {
      await axios.post(`${api}/calorie-log`, {
        date: todayDate,
        food_name: scanResult.name || "Scanned food",
        calories: Number(scanResult.calories) || 0,
        protein: Number(scanResult.protein) || 0,
        carbs: Number(scanResult.carbs) || 0,
        fat: Number(scanResult.fat) || 0,
        serving: scanResult.serving || "1 serving",
      });
      toast.success("Logged from photo!");
      setShowScan(false);
      resetScan();
      fetchCalorieData();
    } catch {
      toast.error("Failed to log scanned food");
    } finally {
      setSavingScan(false);
    }
  };

  // Manual add food
  const saveManualFood = async () => {
    if (!foodForm.food_name.trim()) { toast.error("Food name required"); return; }
    setSavingFood(true);
    try {
      await axios.post(`${api}/calorie-log`, {
        date: todayDate,
        food_name: foodForm.food_name,
        calories: parseFloat(foodForm.calories) || 0,
        protein: parseFloat(foodForm.protein) || 0,
        carbs: parseFloat(foodForm.carbs) || 0,
        fat: parseFloat(foodForm.fat) || 0,
        serving: foodForm.serving || `${servingGrams}g`,
      });
      toast.success("Food logged!");
      setShowAddFood(false);
      setFoodForm({ food_name: "", calories: "", protein: "", carbs: "", fat: "", serving: "" });
      setBasePer100(null);
      setServingGrams("100");
      fetchCalorieData();
    } catch (e) {
      toast.error("Failed to log food");
    } finally {
      setSavingFood(false);
    }
  };

  const deleteEntry = async (id) => {
    try {
      await axios.delete(`${api}/calorie-log/${id}`);
      toast.success("Entry removed");
      fetchCalorieData();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const saveGoal = async () => {
    const val = parseFloat(newGoal);
    if (!val || val <= 0) return;
    try {
      await axios.post(`${api}/calorie-goal`, { daily_calories: val });
      setGoal(val);
      setShowGoalEdit(false);
      toast.success("Calorie goal updated");
    } catch (e) {
      toast.error("Failed to update goal");
    }
  };

  const caloriePercent = goal > 0 ? Math.min((totals.calories / goal) * 100, 100) : 0;
  const isOverGoal = totals.calories > goal;

  return (
    <div className="space-y-6" data-testid="calories-macros-page">
      <div>
        <h2
          className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-white"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          Calories & Macros
        </h2>
        <p className="text-sm text-[#71717A] mt-1">Track your daily intake and look up food nutrition</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#0F0F0F] border border-[#27272A] h-auto">
          <TabsTrigger
            value="tracker"
            className="text-xs uppercase tracking-wider font-bold data-[state=active]:bg-[#E05A1D] data-[state=active]:text-white px-5 py-2"
            data-testid="tab-tracker"
          >
            Calorie Tracker
          </TabsTrigger>
          <TabsTrigger
            value="lookup"
            className="text-xs uppercase tracking-wider font-bold data-[state=active]:bg-[#E05A1D] data-[state=active]:text-white px-5 py-2"
            data-testid="tab-lookup"
          >
            Food Lookup
          </TabsTrigger>
        </TabsList>

        {/* ── Calorie Tracker Tab ── */}
        <TabsContent value="tracker" className="space-y-4 mt-4">
          {/* Date + Add */}
          <div className="flex items-center gap-3">
            <input
              type="date"
              value={todayDate}
              onChange={(e) => setTodayDate(e.target.value)}
              className="bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
              data-testid="calorie-date-picker"
            />
            <div className="flex items-center gap-2 ml-auto">
              <Dialog open={showScan} onOpenChange={(open) => { setShowScan(open); if (!open) resetScan(); }}>
                <DialogTrigger asChild>
                  <button
                    className="bg-[#0F0F0F] border border-[#27272A] hover:border-[#E05A1D] text-white font-bold uppercase tracking-wide px-3 py-2 text-xs flex items-center gap-1.5 transition-all"
                    data-testid="scan-food-btn"
                  >
                    <Camera size={14} weight="bold" /> Scan
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#161616] border-[#27272A] max-w-md" data-testid="scan-food-dialog">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                      <Camera size={20} weight="bold" className="text-[#E05A1D]" /> Scan a meal
                    </DialogTitle>
                  </DialogHeader>

                  {/* Step 1: capture */}
                  {!scanPreview && !scanAnalyzing && (
                    <div className="space-y-3 mt-2" data-testid="scan-empty-state">
                      <p className="text-sm text-[#A1A1AA]">Snap a clear photo of your meal. The AI will identify it and estimate the macros for the visible serving.</p>
                      <label
                        htmlFor="scan-camera-input"
                        className="block w-full cursor-pointer border border-dashed border-[#27272A] hover:border-[#E05A1D] bg-[#0F0F0F] p-8 text-center transition-all"
                        data-testid="scan-camera-label"
                      >
                        <Camera size={32} weight="bold" className="text-[#E05A1D] mx-auto mb-2" />
                        <p className="text-sm font-bold uppercase tracking-wide text-white">Open Camera</p>
                        <p className="text-xs text-[#71717A] mt-1">JPEG or PNG · 1 photo</p>
                      </label>
                      <input
                        id="scan-camera-input"
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => handleScanFile(e.target.files?.[0])}
                        data-testid="scan-camera-input"
                      />
                      <label
                        htmlFor="scan-gallery-input"
                        className="block w-full cursor-pointer text-center text-xs text-[#71717A] hover:text-white font-semibold uppercase tracking-wider transition-colors"
                      >
                        Or pick from gallery
                      </label>
                      <input
                        id="scan-gallery-input"
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={(e) => handleScanFile(e.target.files?.[0])}
                        data-testid="scan-gallery-input"
                      />
                      {scanError && (
                        <div className="border border-red-500/30 bg-red-500/10 p-3">
                          <p className="text-xs text-red-300">{scanError}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 2: analyzing */}
                  {scanAnalyzing && (
                    <div className="space-y-3 mt-2 text-center py-6" data-testid="scan-analyzing">
                      {scanPreview && (
                        <img src={scanPreview} alt="Captured meal" className="max-h-40 mx-auto border border-[#27272A]" />
                      )}
                      <Spinner size={24} className="animate-spin text-[#E05A1D] mx-auto" />
                      <p className="text-sm font-bold uppercase tracking-wide text-white">Reading your plate…</p>
                      <p className="text-xs text-[#71717A]">Identifying the food and estimating macros</p>
                    </div>
                  )}

                  {/* Step 3: result */}
                  {!scanAnalyzing && scanResult && (
                    <div className="space-y-3 mt-2" data-testid="scan-result">
                      <div className="flex gap-3">
                        {scanPreview && (
                          <img src={scanPreview} alt="Captured meal" className="w-20 h-20 object-cover border border-[#27272A] shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-base font-bold text-white truncate" data-testid="scan-result-name">{scanResult.name || "Unknown food"}</p>
                          <p className="text-xs text-[#A1A1AA]">{scanResult.serving || ""}</p>
                          {scanResult.confidence && (
                            <span className={`inline-block mt-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${
                              scanResult.confidence === "high"
                                ? "border-[#22C55E]/40 bg-[#22C55E]/10 text-[#22C55E]"
                                : scanResult.confidence === "medium"
                                  ? "border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]"
                                  : "border-[#71717A]/40 bg-[#71717A]/10 text-[#A1A1AA]"
                            }`} data-testid="scan-confidence">
                              {scanResult.confidence} confidence
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-4 gap-2 text-center">
                        {[
                          ["Calories", scanResult.calories, "kcal", "#E05A1D"],
                          ["Protein", scanResult.protein, "g", "#22C55E"],
                          ["Carbs", scanResult.carbs, "g", "#F59E0B"],
                          ["Fat", scanResult.fat, "g", "#EF4444"],
                        ].map(([label, v, unit, color]) => (
                          <div key={label} className="border border-[#27272A] bg-[#0F0F0F] py-2">
                            <p className="text-[10px] uppercase tracking-wider text-[#71717A]">{label}</p>
                            <p className="text-lg font-black" style={{ color, fontFamily: "'Barlow Condensed', sans-serif" }}>{v ?? "—"}</p>
                            <p className="text-[10px] text-[#71717A]">{unit}</p>
                          </div>
                        ))}
                      </div>

                      {scanResult.notes && (
                        <p className="text-[11px] text-[#71717A] italic border-l-2 border-[#27272A] pl-2">{scanResult.notes}</p>
                      )}

                      {/* Goal-aware nudge */}
                      {scanProjection && (
                        <div
                          className={`border p-3 ${
                            scanProjection.tone === "good" ? "border-[#22C55E]/30 bg-[#22C55E]/5" :
                            scanProjection.tone === "warn" ? "border-[#F59E0B]/30 bg-[#F59E0B]/5" :
                            scanProjection.tone === "bad" ? "border-[#EF4444]/30 bg-[#EF4444]/5" :
                            "border-[#27272A] bg-[#0F0F0F]"
                          }`}
                          data-testid="scan-goal-nudge"
                        >
                          <div className="flex items-baseline justify-between gap-2">
                            <span className={`text-xs font-bold uppercase tracking-wider ${
                              scanProjection.tone === "good" ? "text-[#22C55E]" :
                              scanProjection.tone === "warn" ? "text-[#F59E0B]" :
                              scanProjection.tone === "bad" ? "text-[#EF4444]" :
                              "text-[#A1A1AA]"
                            }`}>
                              {scanProjection.label}
                            </span>
                            <span className="text-[10px] text-[#71717A] tabular-nums">
                              {scanProjection.projected} / {scanProjection.target} kcal
                            </span>
                          </div>
                          <div className="w-full h-1.5 bg-[#27272A] mt-2 overflow-hidden">
                            <div
                              className={`h-full transition-all ${
                                scanProjection.tone === "good" ? "bg-[#22C55E]" :
                                scanProjection.tone === "warn" ? "bg-[#F59E0B]" :
                                scanProjection.tone === "bad" ? "bg-[#EF4444]" :
                                "bg-[#A1A1AA]"
                              }`}
                              style={{ width: `${Math.min(scanProjection.percent, 100)}%` }}
                            />
                          </div>
                          {scanProjection.tip && (
                            <p className="text-[11px] text-[#A1A1AA] mt-2 leading-relaxed">{scanProjection.tip}</p>
                          )}
                        </div>
                      )}

                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={resetScan}
                          className="flex-1 bg-transparent border border-[#27272A] hover:border-[#3f3f46] text-[#A1A1AA] hover:text-white font-bold uppercase tracking-wide py-2 text-xs flex items-center justify-center gap-1.5"
                          data-testid="scan-retake-btn"
                        >
                          <X size={14} weight="bold" /> Retake
                        </button>
                        <button
                          onClick={saveScanResult}
                          disabled={savingScan}
                          className="flex-1 bg-[#E05A1D] hover:bg-[#F06B2E] disabled:opacity-50 text-white font-bold uppercase tracking-wide py-2 text-xs flex items-center justify-center gap-1.5"
                          data-testid="scan-save-btn"
                        >
                          {savingScan ? <Spinner size={14} className="animate-spin" /> : <CheckCircle size={14} weight="bold" />}
                          {savingScan ? "Saving" : "Log Food"}
                        </button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>

              <Dialog open={showAddFood} onOpenChange={setShowAddFood}>
              <DialogTrigger asChild>
                <button
                  className="bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide px-4 py-2 text-xs flex items-center gap-1.5 transition-colors"
                  data-testid="add-food-btn"
                >
                  <Plus size={14} weight="bold" /> Log Food
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[#161616] border-[#27272A] max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold uppercase tracking-tight text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                    Log Food
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                  {/* Food name + AI lookup */}
                  <div>
                    <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Food Name</label>
                    <div className="flex gap-2">
                      <input type="text" value={foodForm.food_name}
                        onChange={(e) => { setFoodForm({ ...foodForm, food_name: e.target.value }); setBasePer100(null); }}
                        onKeyDown={(e) => e.key === "Enter" && lookUpFood()}
                        className="flex-1 bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
                        placeholder="e.g. Chicken Breast" data-testid="food-name-input" />
                      <button onClick={lookUpFood} disabled={lookingUp || !foodForm.food_name.trim()}
                        className="bg-[#27272A] hover:bg-[#3f3f46] text-[#A1A1AA] hover:text-[#E05A1D] text-xs font-bold uppercase px-3 py-2 transition-colors disabled:opacity-40 flex items-center gap-1.5 whitespace-nowrap"
                        data-testid="food-lookup-btn">
                        {lookingUp ? <Spinner size={14} className="animate-spin" /> : <MagnifyingGlass size={14} />}
                        {lookingUp ? "Looking up..." : "Look Up"}
                      </button>
                    </div>
                    {basePer100 && (
                      <p className="text-xs text-[#22C55E] mt-1" data-testid="lookup-success">
                        Found! {basePer100.calories} kcal / {basePer100.protein}g P / {basePer100.carbs}g C / {basePer100.fat}g F per 100g
                      </p>
                    )}
                  </div>

                  {/* Serving size — drives auto-calculation */}
                  <div>
                    <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">
                      Serving Size (grams)
                    </label>
                    <div className="flex gap-2 items-center">
                      <input type="number" value={servingGrams}
                        onChange={(e) => handleServingChange(e.target.value)}
                        className="w-28 bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
                        placeholder="100" min="1" data-testid="food-serving-input" />
                      <span className="text-xs text-[#71717A]">grams</span>
                      {basePer100 && (
                        <div className="flex gap-1 ml-auto">
                          {[50, 100, 150, 200, 250].map(g => (
                            <button key={g} onClick={() => handleServingChange(String(g))}
                              className={`text-xs px-2 py-1 border transition-colors ${
                                servingGrams === String(g) ? "border-[#E05A1D] text-[#E05A1D] bg-[#E05A1D]/10" : "border-[#27272A] text-[#71717A] hover:text-white"
                              }`}
                              data-testid={`serving-preset-${g}`}>
                              {g}g
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Auto-filled / editable macro fields */}
                  <div>
                    <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">
                      Nutrition {basePer100 ? `(calculated for ${servingGrams}g)` : "(manual)"}
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <p className="text-[10px] font-bold uppercase text-[#E05A1D] mb-0.5">Calories</p>
                        <input type="number" value={foodForm.calories} onChange={(e) => setFoodForm({ ...foodForm, calories: e.target.value })}
                          className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-2 py-2 text-sm focus:border-[#E05A1D] outline-none text-center"
                          placeholder="0" data-testid="food-calories-input" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase text-[#22C55E] mb-0.5">Protein</p>
                        <input type="number" value={foodForm.protein} onChange={(e) => setFoodForm({ ...foodForm, protein: e.target.value })}
                          className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-2 py-2 text-sm focus:border-[#E05A1D] outline-none text-center"
                          placeholder="0" data-testid="food-protein-input" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase text-[#F59E0B] mb-0.5">Carbs</p>
                        <input type="number" value={foodForm.carbs} onChange={(e) => setFoodForm({ ...foodForm, carbs: e.target.value })}
                          className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-2 py-2 text-sm focus:border-[#E05A1D] outline-none text-center"
                          placeholder="0" data-testid="food-carbs-input" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase text-[#EF4444] mb-0.5">Fat</p>
                        <input type="number" value={foodForm.fat} onChange={(e) => setFoodForm({ ...foodForm, fat: e.target.value })}
                          className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-2 py-2 text-sm focus:border-[#E05A1D] outline-none text-center"
                          placeholder="0" data-testid="food-fat-input" />
                      </div>
                    </div>
                  </div>

                  <button onClick={saveManualFood} disabled={savingFood}
                    className="w-full bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide py-3 transition-colors disabled:opacity-50"
                    data-testid="save-food-btn">
                    {savingFood ? "Saving..." : "Log Food"}
                  </button>
                </div>
              </DialogContent>
            </Dialog>
            </div>
          </div>

          {/* Recommended Intake Card */}
          {recs && (
            <div className="bg-[#161616] border border-[#27272A] p-5" data-testid="nutrition-recommendations">
              <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Target size={20} className="text-[#E05A1D]" weight="bold" />
                  <span className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A]">
                    Recommended Daily Intake
                  </span>
                </div>
                {recs.goal_label && (
                  <span className="text-[10px] font-bold tracking-[0.1em] uppercase text-[#E05A1D] border border-[#E05A1D]/40 bg-[#E05A1D]/10 px-2 py-1" data-testid="rec-goal-badge">
                    For: {recs.goal_label}
                  </span>
                )}
              </div>

              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-4xl font-black text-[#E05A1D]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }} data-testid="rec-calorie-target">
                  {recs.calories}
                </span>
                <span className="text-sm text-[#71717A]">kcal / day</span>
                {typeof recs.cal_offset === "number" && recs.cal_offset !== 0 && (
                  <span className={`text-[11px] font-bold ml-1 ${recs.cal_offset < 0 ? "text-[#EF4444]" : "text-[#22C55E]"}`} data-testid="rec-calorie-offset">
                    {recs.cal_offset > 0 ? "+" : ""}{recs.cal_offset} vs maintenance
                  </span>
                )}
              </div>
              {typeof recs.maintenance_calories === "number" && (
                <p className="text-[11px] text-[#3f3f46] mb-2">Maintenance: ~{recs.maintenance_calories} kcal</p>
              )}

              <div className="w-full h-3 bg-[#27272A] overflow-hidden" data-testid="rec-calorie-bar">
                <div
                  className={`h-full transition-all duration-500 ${totals.calories > recs.calories ? "bg-[#EF4444]" : "bg-[#E05A1D]"}`}
                  style={{ width: `${Math.min((totals.calories / recs.calories) * 100, 100)}%` }}
                />
              </div>
              <p className="text-xs text-[#71717A] mt-2">
                {totals.calories > recs.calories
                  ? `${Math.round(totals.calories - recs.calories)} kcal over recommendation`
                  : `${Math.round(recs.calories - totals.calories)} kcal under recommendation`}
              </p>

              <div className="grid grid-cols-3 gap-3 mt-4">
                <MacroProgress label="Protein" value={totals.protein} target={recs.protein} unit="g" color="#22C55E" />
                <MacroProgress label="Carbs" value={totals.carbs} target={recs.carbs} unit="g" color="#F59E0B" />
                <MacroProgress label="Fat" value={totals.fat} target={recs.fat} unit="g" color="#EF4444" />
              </div>

              {recs.tip && (
                <div className="border-l-2 border-[#E05A1D] bg-[#E05A1D]/5 pl-3 pr-2 py-2 mt-4" data-testid="rec-goal-tip">
                  <p className="text-xs text-[#A1A1AA] leading-relaxed">{recs.tip}</p>
                </div>
              )}

              <p className="text-[10px] text-[#3f3f46] mt-4">
                Based on {recs.gender}, {recs.age}y, {recs.weight}kg, {recs.height}cm · Mifflin-St Jeor BMR {recs.bmr} kcal · activity x1.55.
                {recs.weight_source?.source === "measurement" && recs.weight_source?.date && (
                  <> · <span className="text-[#22C55E] font-semibold">Auto-updated from your weigh-in on {recs.weight_source.date}</span></>
                )}
              </p>
            </div>
          )}

          {/* Food Log */}
          <div data-testid="food-log">
            <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#71717A] mb-3" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Today's Food Log
            </h3>
            {entries.length === 0 ? (
              <div className="text-center py-10 border border-dashed border-[#27272A]">
                <p className="text-[#71717A] text-sm">No food logged for this day</p>
              </div>
            ) : (
              <div className="space-y-2">
                {entries.map((e) => (
                  <div key={e.id} className="bg-[#161616] border border-[#27272A] p-3 flex items-center justify-between group hover:border-[#3f3f46] transition-colors" data-testid={`food-entry-${e.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-white">{e.food_name}</span>
                        {e.serving && <span className="text-xs text-[#71717A]">{e.serving}</span>}
                      </div>
                      <div className="flex items-center gap-4 mt-1">
                        <span className="text-xs font-bold text-[#E05A1D]">{e.calories} kcal</span>
                        <span className="text-xs text-[#22C55E]">P: {e.protein}g</span>
                        <span className="text-xs text-[#F59E0B]">C: {e.carbs}g</span>
                        <span className="text-xs text-[#EF4444]">F: {e.fat}g</span>
                      </div>
                    </div>
                    <button onClick={() => deleteEntry(e.id)} className="text-[#71717A] hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 p-1" data-testid={`delete-food-${e.id}`}>
                      <Trash size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ── Food Lookup Tab ── */}
        <TabsContent value="lookup" className="space-y-4 mt-4">
          <div className="flex gap-3" data-testid="macro-search-bar">
            <div className="flex-1 relative">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
              <input
                type="text" value={query} onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchFood()}
                className="w-full bg-[#0F0F0F] border border-[#27272A] text-white pl-10 pr-4 py-3 text-sm focus:border-[#E05A1D] focus:ring-1 focus:ring-[#E05A1D] outline-none transition-all"
                placeholder="Search food (e.g. chicken breast, rice, banana...)"
                data-testid="macro-search-input" />
            </div>
            <button onClick={searchFood} disabled={loading || !query.trim()}
              className="bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide px-6 py-3 text-sm transition-colors disabled:opacity-50 flex items-center gap-2"
              data-testid="macro-search-btn">
              {loading ? <Spinner size={16} className="animate-spin" /> : "Search"}
            </button>
          </div>

          {rawText && (
            <div className="bg-[#161616] border border-[#27272A] p-5" data-testid="macro-raw-response">
              <pre className="text-sm text-[#A1A1AA] whitespace-pre-wrap">{rawText}</pre>
            </div>
          )}

          {results.length > 0 && (
            <div className="space-y-3" data-testid="macro-results">
              {results.map((food, idx) => (
                <div key={idx} className="bg-[#161616] border border-[#27272A] p-5" data-testid={`macro-result-${idx}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold tracking-tight uppercase text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                      {food.name}
                    </h3>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#71717A]">{food.serving || "per 100g"}</span>
                      <button onClick={() => addToLog(food)}
                        className="bg-[#E05A1D] hover:bg-[#F06B2E] text-white text-xs font-bold uppercase px-3 py-1.5 flex items-center gap-1 transition-colors"
                        data-testid={`add-to-log-${idx}`}>
                        <Plus size={12} weight="bold" /> Log
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <MacroCard label="Calories" value={food.calories} unit="kcal" color="#E05A1D" />
                    <MacroCard label="Protein" value={food.protein} unit="g" color="#22C55E" />
                    <MacroCard label="Carbs" value={food.carbs} unit="g" color="#F59E0B" />
                    <MacroCard label="Fat" value={food.fat} unit="g" color="#EF4444" />
                    <MacroCard label="Fiber" value={food.fiber} unit="g" color="#A1A1AA" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {results.length === 0 && !loading && !rawText && (
            <div className="text-center py-16 border border-dashed border-[#27272A]">
              <MagnifyingGlass size={48} className="text-[#27272A] mx-auto mb-3" />
              <p className="text-[#71717A] text-sm">Search for any food to see its macros</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MacroCard({ label, value, unit, color }) {
  return (
    <div className="border border-[#27272A] p-3 text-center bg-[#0F0F0F]">
      <p className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1">{label}</p>
      <p className="text-2xl font-black" style={{ fontFamily: "'Barlow Condensed', sans-serif", color }}>{value ?? "—"}</p>
      <p className="text-xs text-[#71717A]">{unit}</p>
    </div>
  );
}

function MacroMini({ label, value, unit, color }) {
  return (
    <div className="text-center">
      <p className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A]">{label}</p>
      <p className="text-xl font-black" style={{ fontFamily: "'Barlow Condensed', sans-serif", color }}>{Math.round(value)}</p>
      <p className="text-xs text-[#71717A]">{unit}</p>
    </div>
  );
}

function MacroProgress({ label, value, target, unit, color }) {
  const pct = target ? Math.min((value / target) * 100, 100) : 0;
  const isOver = target && value > target;
  return (
    <div data-testid={`macro-progress-${label.toLowerCase()}`}>
      <div className="flex items-baseline justify-between mb-1">
        <p className="text-xs font-bold tracking-[0.1em] uppercase" style={{ color }}>{label}</p>
        {target && (
          <p className="text-[10px] text-[#3f3f46]">/ {target}{unit}</p>
        )}
      </div>
      <p className="text-xl font-black text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
        {Math.round(value)}<span className="text-xs text-[#71717A] ml-0.5">{unit}</span>
      </p>
      {target && (
        <div className="w-full h-1.5 bg-[#27272A] mt-1.5 overflow-hidden">
          <div
            className="h-full transition-all duration-500"
            style={{ width: `${pct}%`, backgroundColor: isOver ? '#EF4444' : color }}
          />
        </div>
      )}
    </div>
  );
}

function RecCard({ label, value, unit, color, current }) {
  const pct = value > 0 ? Math.min((current / value) * 100, 100) : 0;
  const isOver = current > value;
  return (
    <div className="border border-[#27272A] p-3 bg-[#0F0F0F]" data-testid={`rec-${label.toLowerCase()}`}>
      <p className="text-[10px] font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1">{label}</p>
      <p className="text-2xl font-black" style={{ fontFamily: "'Barlow Condensed', sans-serif", color }}>{value}</p>
      <p className="text-[10px] text-[#71717A]">{unit}</p>
      <div className="w-full h-1.5 bg-[#27272A] mt-2 overflow-hidden">
        <div
          className="h-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: isOver ? '#EF4444' : color }}
        />
      </div>
      <p className="text-[10px] mt-1" style={{ color: isOver ? '#EF4444' : '#71717A' }}>
        {Math.round(current)} / {value} {unit}
      </p>
    </div>
  );
}
